"use client";

import type React from "react";
import { type FC, useState, useRef, useCallback, useEffect, useMemo } from "react";
import Modal from "../Modal";
import ModalContent from "../Modal/ModalContent";
import styles from "./Currencies.module.scss";
import { useModal } from "@hooks/useModal";
import { createModalHelpers } from "@stores/unified-modal.store";
import cn from "classnames";
import Input from "@components/ui/input";
import { useWallet } from "@stores/wallet.store";
import { useAssets } from "@stores/assets.store";
import type { IAsset } from "@api/IWalletController";
import Separator from "@components/ui/separator";
import CurrencyItem from "@components/shared/CurrencyForm/CurrencyItem";

// Компонент скелетона для имитации CurrencyItem во время загрузки
const CurrencyItemSkeleton: FC = () => {
    return (
        <div className="w-full flex items-center p-3 rounded-2xl border bg-muted-foreground/10 animate-pulse space-x-3">
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-600/50"></div>
            <div className="flex-grow">
                <div className="h-4 w-3/4 bg-gray-600/50 rounded mb-2"></div>
                <div className="h-3 w-1/2 bg-gray-600/50 rounded"></div>
            </div>
            <div className="h-4 w-16 bg-gray-600/50 rounded"></div>
        </div>
    );
};

interface Props {
    setAsset: (asset: IAsset) => void;
    type: "Sell" | "Buy";
}

const Currencies: FC<Props> = ({ setAsset, type }) => {
    const { currencies, openModal, closeModal } = createModalHelpers();
    const [handleClose] = useModal(() => closeModal("currencies"));

    const portfolioItems = useWallet((state) => state.portfolioItems);

    const handleSetCurrency = (asset: IAsset) => {
        setAsset(asset);
        handleClose();
    };

    const [searchQuery, setSearchQuery] = useState("");
    const { assets, fetchAssets, findAssets } = useAssets((state) => ({
        assets: state.assets,
        fetchAssets: state.fetchAssets,
        findAssets: state.findAssets,
    }));

    const assetsContainerRef = useRef<HTMLDivElement | null>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastSearchRef = useRef<string>("");
    const isSearchingRef = useRef<boolean>(false);
    const isScrollLoadingRef = useRef<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    // For updating the list when scrolling
    const handleScroll = useCallback(() => {
        if (assetsContainerRef.current) {
            const bottom =
                Math.abs(
                    assetsContainerRef.current.scrollHeight -
                        assetsContainerRef.current.scrollTop -
                        assetsContainerRef.current.clientHeight,
                ) < 1;

            if (bottom && !isScrollLoadingRef.current) {
                if (isLoading) return;

                setIsLoading(true);
                isScrollLoadingRef.current = true;
                fetchAssets(true).finally(() => {
                    setIsLoading(false);
                    setTimeout(() => {
                        isScrollLoadingRef.current = false;
                    }, 300);
                });
            }
        }
    }, [fetchAssets, isLoading]);

    // Filtering assets based on search query
    const filteredAssets = assets.filter(
        (asset) =>
            asset.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            asset.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            asset.jettonMasterAddress.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    // Debounced search effect - separate from assets dependency to prevent loops
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchQuery.length === 0) {
            lastSearchRef.current = "";
            isSearchingRef.current = false;
            return;
        }

        if (searchQuery === lastSearchRef.current || isSearchingRef.current) {
            return;
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (filteredAssets.length === 0) {
                lastSearchRef.current = searchQuery;
                isSearchingRef.current = true;

                const searchPromise =
                    searchQuery.length > 20
                        ? findAssets([{ jettonMasterAddress: searchQuery }])
                        : findAssets([{ symbol: searchQuery }]);

                searchPromise.finally(() => {
                    isSearchingRef.current = false;
                });
            }
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, filteredAssets.length, findAssets]);

    // Sorting tokens by balance if type === "Sell"
    const sortedAssets: IAsset[] = filteredAssets.sort((a, b) => {
        if (type === "Sell") {
            const balanceA =
                portfolioItems.find((item) => item.jettonMasterAddress === a.jettonMasterAddress)?.balance || 0;
            const balanceB =
                portfolioItems.find((item) => item.jettonMasterAddress === b.jettonMasterAddress)?.balance || 0;
            return balanceB - balanceA;
        }
        return 0;
    });

    const pinnedAssets = [sortedAssets[0], sortedAssets[1]].filter((asset): asset is IAsset => !!asset);

    const assetsWithoutPinned = sortedAssets.filter((asset) => !pinnedAssets.includes(asset));

    const nonPinnedWithBalance = useMemo(
        () =>
            assetsWithoutPinned
                .map((asset) => {
                    const pItem = portfolioItems.find((pi) => pi.jettonMasterAddress === asset.jettonMasterAddress);
                    return { asset, balance: pItem?.balance ?? 0 };
                })
                .filter(({ balance }) => balance > 0)
                .sort((a, b) => b.balance - a.balance)
                .map(({ asset }) => asset),
        [assetsWithoutPinned, portfolioItems],
    );

    const nonPinnedWithoutBalance = useMemo(
        () =>
            assetsWithoutPinned.filter(
                (asset) =>
                    !nonPinnedWithBalance.some((npAsset) => npAsset.jettonMasterAddress === asset.jettonMasterAddress),
            ),
        [assetsWithoutPinned, nonPinnedWithBalance],
    );

    const isEmpty = useMemo(() => sortedAssets.length === 2, [sortedAssets]);

    useEffect(() => {
        if (isEmpty) {
            fetchAssets(true);
        }
    }, [isEmpty, fetchAssets]);

    return (
        <Modal
            isVisible={currencies}
            setIsVisible={(isVisible) => (isVisible ? openModal("currencies") : closeModal("currencies"))}
            classNames="w-full h-full flex justify-center items-center"
        >
            <ModalContent classNames={cn(styles.content, styles.fadeIn, styles.dropIn, "mt-10")}>
                <div className={styles.header}>
                    <h2 className={styles.headline}>Select token</h2>
                    <button className={styles.close} onClick={handleClose}>
                        <picture>
                            <source
                                srcSet="/images/modal/closemobile.svg"
                                width={7.5}
                                height={7.5}
                                media="(max-width: 768px)"
                            />
                            <img
                                height={12}
                                width={12}
                                src="/images/modal/close.svg"
                                style={{ opacity: 0.4 }}
                                alt="Close modal"
                            />
                        </picture>
                    </button>
                </div>
                <div className={styles.inputHolder}>
                    <Input
                        autoComplete="new-password"
                        value={searchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        name="Search"
                        placeholder="Enter name or coin address"
                        classNames={styles.input}
                    />
                    <img src="/images/scam/search.svg" alt="" />
                </div>
                <div
                    className="flex-grow overflow-y-auto w-full max-w-full overflow-x-hidden flex-1"
                    ref={assetsContainerRef}
                    onScroll={handleScroll}
                >
                    {pinnedAssets.length > 0 && (
                        <>
                            {pinnedAssets.map((asset) => (
                                <CurrencyItem
                                    key={asset.jettonMasterAddress}
                                    asset={asset}
                                    portfolioItem={portfolioItems.find(
                                        (item) => item.jettonMasterAddress === asset.jettonMasterAddress,
                                    )}
                                    onSelect={() => handleSetCurrency(asset)}
                                />
                            ))}
                            {(nonPinnedWithBalance.length > 0 || nonPinnedWithoutBalance.length > 0) && <Separator />}
                        </>
                    )}

                    {nonPinnedWithBalance.length > 0 && (
                        <>
                            {nonPinnedWithBalance.map((asset) => (
                                <CurrencyItem
                                    key={asset.jettonMasterAddress}
                                    asset={asset}
                                    portfolioItem={portfolioItems.find(
                                        (item) => item.jettonMasterAddress === asset.jettonMasterAddress,
                                    )}
                                    onSelect={() => handleSetCurrency(asset)}
                                />
                            ))}
                            {nonPinnedWithoutBalance.length > 0 && <Separator />}
                        </>
                    )}

                    {nonPinnedWithoutBalance.length > 0 && (
                        <>
                            {nonPinnedWithoutBalance.map((asset) => (
                                <CurrencyItem
                                    key={asset.jettonMasterAddress}
                                    asset={asset}
                                    portfolioItem={portfolioItems.find(
                                        (item) => item.jettonMasterAddress === asset.jettonMasterAddress,
                                    )}
                                    onSelect={() => handleSetCurrency(asset)}
                                />
                            ))}
                        </>
                    )}

                    {isLoading && (
                        <>
                            <Separator />
                            {Array.from({ length: 5 }).map((_, index) => (
                                <CurrencyItemSkeleton key={`skeleton-${index}`} />
                            ))}
                        </>
                    )}
                </div>
            </ModalContent>
        </Modal>
    );
};

export default Currencies;
